// hooks/candidatures/useCandidatures.ts
import { useState, useEffect, useCallback } from "react";
import { BaseCandidature, Candidature } from "../../../types/features/Candidature";
import { candidatureService } from "../../../services/candidatureService";
import useToken from "../auth/useToken";

export default function useCandidatures() {
  const { token } = useToken();
  const [candidatures, setCandidatures] = useState<Candidature[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔒 PROTÉGÉ - Token requis (admin)
  const fetchCandidatures = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!token) {
      setError("Vous devez être connecté");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await candidatureService.getAll(token, page, perPage);
      setCandidatures(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des candidatures");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis (admin)
  const fetchCandidatureById = useCallback(async (id: string): Promise<Candidature | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const candidature = await candidatureService.getById(token, id);
      return candidature;
    } catch (err) {
      setError("Erreur lors du chargement de la candidature");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ✅ NOUVEAU - 🔒 PROTÉGÉ - Récupérer les candidatures par product_name
  const fetchCandidaturesByProduct = useCallback(async (
    productName: string,
    page: number = 1,
    perPage: number = 20
  ) => {
    if (!token) {
      setError("Vous devez être connecté");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await candidatureService.getByProductName(token, productName, page, perPage);
      setCandidatures(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des candidatures par produit");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ✅ NOUVEAU - 🔒 PROTÉGÉ - Récupérer les candidatures par offre d'emploi
  const fetchCandidaturesByJobOffer = useCallback(async (
    jobOfferId: string,
    page: number = 1,
    perPage: number = 20
  ) => {
    if (!token) {
      setError("Vous devez être connecté");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await candidatureService.getByJobOffer(token, jobOfferId, page, perPage);
      setCandidatures(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des candidatures pour cette offre");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔓 PUBLIC - Pas de token nécessaire
  const createCandidature = useCallback(async (formData: BaseCandidature): Promise<Candidature | null> => {
    setLoading(true);
    setError(null);
    try {
      const newCandidature = await candidatureService.create(formData);
      return newCandidature;
    } catch (err) {
      setError("Erreur lors de l'envoi de la candidature");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const updateCandidature = useCallback(
    async (id: string, status: string, notes?: string): Promise<Candidature | null> => {
      if (!token) {
        setError("Vous devez être connecté");
        return null;
      }

      setLoading(true);
      setError(null);
      try {
        const updated = await candidatureService.update(token, id, { status, notes });
        setCandidatures((prev) => prev.map((c) => (c.id === id ? updated : c)));
        return updated;
      } catch (err) {
        setError("Erreur lors de la mise à jour");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // 🔒 PROTÉGÉ - Token requis
  const deleteCandidature = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await candidatureService.delete(token, id);
      setCandidatures((prev) => prev.filter((c) => c.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyCandidatures = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await candidatureService.deleteMany(token, ids);
      setCandidatures((prev) => prev.filter((c) => !ids.includes(c.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCandidatures();
    }
  }, [token, fetchCandidatures]);

  return {
    candidatures,
    loading,
    error,
    pagination,
    fetchCandidatures,
    fetchCandidatureById,
    fetchCandidaturesByProduct,
    fetchCandidaturesByJobOffer,
    createCandidature,
    updateCandidature,
    deleteCandidature,
    deleteManyCandidatures,
  };
}